import * as React from "react";

import { RobotoLLMContext } from "@/shared/domain/chat/ChatRecord";

import { type ContextType } from "./ChatInputBox/RobotoLLMContextWidget";
import {
  ContextDisplayNames,
  useRobotoLLMContext,
} from "./hooks/useRobotoLLMContext";

const CHAT_ID_STORAGE_KEY = "roboto-ai-chat-current-chat-id";

/**
 * AIChatContext provides UI state only - no data fetching or mutations.
 * Data fetching and mutations are handled by the Chat component.
 */
interface AIChatContextValue {
  // UI state
  isFullscreen: boolean;
  showHistory: boolean;
  autoNavEnabled: boolean;
  currentChatId: string | undefined;
  context: RobotoLLMContext;
  contextDisplayNames: ContextDisplayNames;

  // UI state setters
  setIsFullscreen: (value: boolean) => void;
  setShowHistory: (value: boolean) => void;
  setAutoNavEnabled: (value: boolean) => void;
  setCurrentChatId: (chatId: string | undefined) => void;

  // Convenience handlers
  handleContextDelete: (
    contextType: ContextType,
    contextContent: string,
  ) => void;
  handleNewChat: () => void;
  handleToggleFullscreen: () => void;
  handleToggleHistory: () => void;
  handleToggleAutoNav: () => void;
  handleHistoryChatClicked: (chatId: string) => void;
  handleLinkClick: () => void;
  handleResetUIState: () => void;
}

const AIChatContext = React.createContext<AIChatContextValue | undefined>(
  undefined,
);

interface AIChatProviderProps {
  children: React.ReactNode;
}

export const AIChatProvider: React.FC<AIChatProviderProps> = ({ children }) => {
  // UI state
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [showHistory, setShowHistory] = React.useState(false);
  const [autoNavEnabled, setAutoNavEnabled] = React.useState(() => {
    const stored = localStorage.getItem("autoNavEnabled");
    return stored === "true";
  });

  const { context, displayNames, handleContextDelete } = useRobotoLLMContext();

  // Chat ID state with sessionStorage persistence
  const [currentChatId, setCurrentChatId] = React.useState<string | undefined>(
    () => {
      try {
        return sessionStorage.getItem(CHAT_ID_STORAGE_KEY) || undefined;
      } catch {
        return undefined;
      }
    },
  );

  // Persist chat ID to sessionStorage whenever it changes
  React.useEffect(() => {
    if (currentChatId) {
      try {
        sessionStorage.setItem(CHAT_ID_STORAGE_KEY, currentChatId);
      } catch {
        // Ignore sessionStorage errors
      }
    } else {
      try {
        sessionStorage.removeItem(CHAT_ID_STORAGE_KEY);
      } catch {
        // Ignore sessionStorage errors
      }
    }
  }, [currentChatId]);

  // UI State Handlers
  const handleNewChat = () => {
    setCurrentChatId(undefined);
    setShowHistory(false);
  };

  const handleToggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  const handleToggleHistory = () => {
    setShowHistory((prev) => !prev);
  };

  const handleToggleAutoNav = () => {
    setAutoNavEnabled((prev) => {
      const newValue = !prev;
      localStorage.setItem("autoNavEnabled", String(newValue));
      return newValue;
    });
  };

  const handleHistoryChatClicked = (chatId: string) => {
    setCurrentChatId(chatId);
    setShowHistory(false);
  };

  const handleLinkClick = () => {
    setIsFullscreen(false);
  };

  const handleResetUIState = () => {
    setShowHistory(false);
    setIsFullscreen(false);
  };

  const value: AIChatContextValue = {
    // UI state
    isFullscreen,
    showHistory,
    autoNavEnabled,
    currentChatId,
    context,
    contextDisplayNames: displayNames,

    // UI state setters
    setIsFullscreen,
    setShowHistory,
    setAutoNavEnabled,
    setCurrentChatId,

    // Convenience handlers
    handleContextDelete,
    handleNewChat,
    handleToggleFullscreen,
    handleToggleHistory,
    handleToggleAutoNav,
    handleHistoryChatClicked,
    handleLinkClick,
    handleResetUIState,
  };

  return (
    <AIChatContext.Provider value={value}>{children}</AIChatContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAIChat = (): AIChatContextValue => {
  const context = React.useContext(AIChatContext);
  if (context === undefined) {
    throw new Error("useAIChat must be used within an AIChatProvider");
  }
  return context;
};
